import { useEffect, useState } from "react";
import { Camera, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, imgSrc, CATEGORIES, STORES } from "@/lib/api";

const EMPTY = { name: "", description: "", category: CATEGORIES[0], store: STORES[0], price: "", image_url: "", weight: "", weight_kg: 0.5, in_stock: true, featured: false };
const inputCls = "mt-1 w-full rounded-lg border border-[#E2D7C3] bg-white px-3 py-2 text-sm";

export default function ProductForm({ open, initial, onClose, onSave }) {
  const [f, setF] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { setF({ ...EMPTY, ...(initial || {}) }); }, [initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!f.image_url) return toast.error("Please add a product photo");
    const { id, created_at, ...rest } = f;
    onSave({ ...rest, price: parseFloat(f.price), weight_kg: parseFloat(f.weight_kg), stock_qty: f.stock_qty === "" || f.stock_qty == null ? null : parseInt(f.stock_qty, 10) });
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setF((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Photo uploaded");
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#FDFBF7]" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{initial?.id ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3 text-sm" data-testid="product-form">
          <label className="col-span-2">Name<input required value={f.name} onChange={set("name")} className={inputCls} data-testid="product-form-name" /></label>
          <div className="col-span-2" data-testid="product-photo-field">
            <span>Photo</span>
            <div className="mt-1 flex items-center gap-3">
              <div className="w-20 h-20 rounded-lg bg-[#F7F3EB] border border-[#E2D7C3] grid place-items-center overflow-hidden">
                {f.image_url ? <img src={imgSrc(f.image_url)} alt="" className="w-full h-full object-contain" data-testid="product-form-image-preview" /> : <ImagePlus className="w-6 h-6 text-slate-400" />}
              </div>
              <label className="cursor-pointer rounded-full bg-[#1B3626] text-white px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="product-form-upload-button">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                {uploading ? "Uploading…" : "Take or choose photo"}
                <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} data-testid="product-form-file-input" />
              </label>
            </div>
            <input value={f.image_url} onChange={set("image_url")} className={inputCls} placeholder="…or paste an image link" data-testid="product-form-image" />
          </div>
          <label>Category<select value={f.category} onChange={set("category")} className={inputCls} data-testid="product-form-category">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></label>
          <label>Bought from<select value={f.store} onChange={set("store")} className={inputCls} data-testid="product-form-store">{STORES.map((c) => <option key={c}>{c}</option>)}</select></label>
          <label>Price (£)<input required type="number" step="0.01" min="0.01" value={f.price} onChange={set("price")} className={inputCls} data-testid="product-form-price" /></label>
          <label>Size shown to customers<input value={f.weight} onChange={set("weight")} className={inputCls} placeholder="e.g. 400g" data-testid="product-form-weight" /></label>
          <label>Packed weight (kg) for postage<input required type="number" step="0.01" min="0.01" max="30" value={f.weight_kg} onChange={set("weight_kg")} className={inputCls} data-testid="product-form-weight-kg" /></label>
          <label>Stock quantity<input type="number" step="1" min="0" value={f.stock_qty ?? ""} onChange={set("stock_qty")} className={inputCls} placeholder="Leave empty for no limit" data-testid="product-form-stock-qty" /></label>
          <label className="col-span-2">Description<textarea rows={3} value={f.description} onChange={set("description")} className={inputCls} data-testid="product-form-description" /></label>
          <label className="col-span-2">Ingredients<textarea rows={3} value={f.ingredients || ""} onChange={set("ingredients")} className={inputCls} data-testid="product-form-ingredients" /></label>
          <label className="col-span-2">Allergen information<textarea rows={2} value={f.allergens || ""} onChange={set("allergens")} className={inputCls} data-testid="product-form-allergens" /></label>
          <label className="flex items-center gap-2"><Switch checked={f.in_stock} onCheckedChange={(v) => setF({ ...f, in_stock: v })} data-testid="product-form-in-stock" /> In stock</label>
          <label className="flex items-center gap-2"><Switch checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} data-testid="product-form-featured" /> Featured</label>
          <button className="col-span-2 mt-2 rounded-full bg-[#8C2220] text-white py-3 font-semibold" data-testid="product-form-save">Save product</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
